<template>
  <div :class="[staticClass, className]" class="sf-sidebar">
    <transition :name="transitionName">
      <aside v-if="visible" ref="asideContent" v-click-outside="checkPersistence" v-focus-trap
        class="sf-sidebar__aside pictime-sidebar">
        <slot name="bar">
          <PictimeBar :close="true" :title="title" class="smartphone-only" @click:close="close" />
        </slot>

        <slot name="circle-icon" v-bind="{ close, button }">
          <SfCircleIcon :class="{ 'display-none': !button }" aria-label="Close sidebar"
            class="sf-sidebar__circle-icon desktop-only" icon="cross" icon-size="12px" @click="close" />
        </slot>
        <div :class="{ 'display-none': !title || (!title && !hasTop) }" class="sf-sidebar__top">
          <slot name="title" v-bind="{ title, subtitle, headingLevel }">
            <SfHeading :class="{ 'display-none': !title }" :description="subtitle" :level="headingLevel" :title="title"
              class="
                                sf-heading--left sf-heading--no-underline
                                sf-sidebar__title
                                desktop-only
                            " />
          </slot>
          <slot name="content-top" />
        </div>
        <div class="sf-sidebar__content">
          <slot />
        </div>
        <div :class="{ 'display-none': !hasBottom }" class="sf-sidebar__bottom">
          <slot name="content-bottom" />
        </div>
      </aside>
    </transition>
  </div>
</template>
<script>
import { clickOutside, focusTrap } from "@storefront-ui/vue/src/utilities/directives/";
import { clearAllBodyScrollLocks, disableBodyScroll } from "body-scroll-lock";
import { isClient } from "@storefront-ui/vue/src/utilities/helpers";
import PictimeBar from "../molecules/PictimeBar.vue";
import SfCircleIcon from "@storefront-ui/vue/src/components/atoms/SfCircleIcon/SfCircleIcon.vue";
import SfOverlay from "@storefront-ui/vue/src/components/atoms/SfOverlay/SfOverlay.vue";
import SfHeading from "@storefront-ui/vue/src/components/atoms/SfHeading/SfHeading.vue";

export default {
  name: "PictimeSidebar",
  directives: { focusTrap, clickOutside },
  components: {
    PictimeBar,
    SfCircleIcon,
    SfOverlay,
    SfHeading,
  },
  props: {
    title: {
      type: String,
      default: "",
    },
    subtitle: {
      type: String,
      default: "",
    },
    headingLevel: {
      type: Number,
      default: 3,
    },
    button: {
      type: Boolean,
      default: true,
    },
    visible: {
      type: Boolean,
      default: false,
    },
    overlay: {
      type: Boolean,
      default: true,
    },
    persistent: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      position: "left",
      staticClass: null,
      className: null,
    };
  },
  computed: {
    visibleOverlay() {
      return this.visible && this.overlay;
    },
    transitionName() {
      return "sf-slide-" + this.position;
    },
    hasTop() {
      return this.$slots.hasOwnProperty("content-top");
    },
    hasBottom() {
      return this.$slots.hasOwnProperty("content-bottom");
    },
  },
  watch: {
    visible: {
      handler(value) {
        if (!isClient) return;
        if (value) {
          this.$nextTick(() => {
            const sidebarContent = document.getElementsByClassName(
              "sf-sidebar__content"
            )[0];
            disableBodyScroll(sidebarContent);
          });
          document.addEventListener("keydown", this.keydownHandler);
        } else {
          clearAllBodyScrollLocks();
          document.removeEventListener("keydown", this.keydownHandler);
        }
      },
      immediate: true,
    },
  },
  mounted() {
    this.classHandler();
  },
  updated() {
    this.classHandler();
  },
  beforeDestroy() {
    clearAllBodyScrollLocks();
  },
  methods: {
    close() {
      this.$emit("close");
    },
    checkPersistence() {
      if (!this.persistent) this.close();
    },
    keydownHandler(e) {
      if (e.key === "Escape" || e.key === "Esc" || e.keyCode === 27) {
        this.close();
      }
    },
    classHandler() {
      let update = false;
      if (this.staticClass !== this.$vnode.data.staticClass) {
        this.staticClass = this.$vnode.data.staticClass;
        update = true;
      }
      if (this.className !== this.$vnode.data.class) {
        this.className = this.$vnode.data.class;
        update = true;
      }
      if (update) {
        this.position =
          [this.staticClass, this.className].toString().search("--right") > -1
            ? "right"
            : "left";
      }
    },
  },
};
</script>
<style lang="scss">
@import "~@storefront-ui/shared/styles/components/organisms/SfSidebar.scss";
</style>
